// src/services/analyticsService.js

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-MQ8QCDJWT1';
const CONSENT_COOKIE_NAME = 'lucent-analytics-consent';

// Service state
let isInitialized = false;

/**
 * Initializes the analytics service
 * Should be called as early as possible in app lifecycle
 */
export const initAnalytics = () => {
  // Only initialize once
  if (isInitialized) return;
  isInitialized = true;

  // Check for existing consent
  if (hasStoredConsent()) {
    loadGoogleAnalytics();
  }
};

/**
 * Checks if user has previously given consent
 * @returns {boolean} True if consent has been given
 */
export const hasStoredConsent = () => {
  return document.cookie.includes(`${CONSENT_COOKIE_NAME}=true`);
};

/**
 * Save user consent decision and load analytics if accepted
 * @param {boolean} accepted User's consent decision
 */
export const setConsent = (accepted) => {
  // Set consent status
  if (accepted) {
    // Set consent cookie
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 150); // 150 days expiry
    document.cookie = `${CONSENT_COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/`;
    
    // Load analytics if it hasn't been loaded yet
    loadGoogleAnalytics();
  } else {
    // Set declined cookie with shorter expiry
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30); // 30 days expiry for declined choice
    document.cookie = `${CONSENT_COOKIE_NAME}=false; expires=${expiryDate.toUTCString()}; path=/`;
    
    // Clear any existing Google Analytics cookies
    clearAnalyticsCookies();
  }
};

/**
 * Load the Google Analytics scripts
 */
export const loadGoogleAnalytics = () => {
  // Don't load if already loaded
  if (window.gaLoaded) return;
  console.log("Loading Google Analytics...");
  
  try {
    // Load the Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    
    // Initialize the dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      'anonymize_ip': true,  // Enable IP anonymization for GDPR
      'allow_google_signals': false  // Disable Google signals for more privacy
    });
    
    // Set window.gtag function
    window.gtag = gtag;
    window.gaLoaded = true;
    
    // Dispatch event that analytics has loaded
    const event = new Event('analytics-loaded');
    document.dispatchEvent(event);
    
    console.log("Google Analytics loaded successfully");
  } catch (error) {
    console.error("Error loading Google Analytics:", error);
  }
};

/**
 * Clear all Google Analytics cookies
 */
export const clearAnalyticsCookies = () => {
  const cookiesToClear = ['_ga', '_gat', '_gid', '_ga_']; // Google Analytics cookie prefixes
  
  document.cookie.split(';').forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    cookiesToClear.forEach(prefix => {
      if (cookieName.startsWith(prefix)) {
        // Set expiry to past date to clear the cookie
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        console.log(`Cleared analytics cookie: ${cookieName}`);
      }
    });
  });
};

/**
 * Track page views
 * @param {string} path - Current page path
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  if (!window.gaLoaded) return;
  
  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title
    });
    console.log(`Page view tracked: ${path} - ${title}`);
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
};

/**
 * Track custom events
 * @param {string} category - Event category
 * @param {string} action - Event action 
 * @param {string} label - Event label
 */
export const trackEvent = (category, action, label) => {
  if (!window.gaLoaded) return;
  
  try {
    window.gtag('event', action, {
      'event_category': category,
      'event_label': label
    });
    console.log(`Event tracked: ${category} / ${action} / ${label}`);
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};